'use client';

import {
  Alert,
  Box,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import type {
  PrivateLinkInstructionStep,
  RdsSecurityGroup,
} from '@repo/api-gw-sdk';

import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { CopyToClipboardButton } from '@/components/buttons/copyToClipboardButton';
import { Loader } from '@/components/layout/loading';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import type { RdsCdcWizardState } from '../rdsCdcWizardState';

const subtitles: Record<RdsCdcWizardState['format'], string> = {
  'aws-cli':
    'Copy and run the script below in your terminal to set up PrivateLink connectivity. Requires aws-cli v2 and jq.',
  cloudformation:
    'Deploy the following CloudFormation template in your account to set up PrivateLink connectivity.',
  terraform:
    'Use the following Terraform configuration to set up PrivateLink connectivity.',
};

const isUrl = (value: string) => value.startsWith('https://');

const StepCommandContent = ({
  cmd,
  selectedSgId,
  downloadLink,
}: {
  cmd: string;
  selectedSgId: string;
  downloadLink?: string;
}) => {
  if (isUrl(cmd)) {
    return (
      <ButtonWithIcon
        variant='outlined'
        icon='material-symbols-open-in-new'
        iconPosition='right'
        text='Open AWS CloudFormation Console'
        disabled={!selectedSgId}
        disabledTooltip='Select a database security group to continue'
        onClick={() => window.open(cmd, '_blank', 'noopener')}
        sx={{ alignSelf: 'flex-start' }}
      />
    );
  }

  return (
    <Stack gap='8px'>
      <Stack direction='row' alignItems='flex-start' gap='8px'>
        <Typography
          variant='body1'
          component='code'
          sx={{
            flex: 1,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            fontFamily: 'monospace',
          }}
        >
          {cmd}
        </Typography>
        <CopyToClipboardButton textToCopy={cmd} />
      </Stack>
      {downloadLink && (
        <ButtonWithIcon
          variant='outlined'
          icon='material-symbols-download-rounded'
          iconPosition='right'
          text='Download CloudFormation Template'
          onClick={() => window.open(downloadLink, '_blank', 'noopener')}
          sx={{ alignSelf: 'flex-start' }}
        />
      )}
    </Stack>
  );
};

export const AwsPrivateLinkStep = (props: StepProps<RdsCdcWizardState>) => {
  const {
    wizardState: state,
    setWizardState,
    currentStep,
    setNextStep,
  } = props;
  const dal = useDAL();
  const {
    data,
    isLoading: loading,
    error,
  } = dal.inventory.privateLink.useOnboardRdsCdc(state.resourceId);

  const securityGroups: RdsSecurityGroup[] = data?.rdsSecurityGroups ?? [];
  const selectedSgId =
    state.selectedSecurityGroupId || securityGroups[0]?.id || '';

  const rdsSgPlaceholder = '<RDS_SECURITY_GROUP_ID>';
  const substituteRdsSg = (command: string) =>
    selectedSgId
      ? command
          .replaceAll(rdsSgPlaceholder, selectedSgId)
          .replaceAll(
            encodeURIComponent(rdsSgPlaceholder),
            encodeURIComponent(selectedSgId)
          )
      : command;

  const currentSteps =
    data?.privateLinkInstructions?.[state.format]?.steps ?? [];

  return (
    <StepContainer
      stepperIndex={0}
      canGoNext={() => currentSteps.length > 0 && !!selectedSgId}
      nextButtonText='Next'
      onNextClick={async () => {
        setNextStep(currentStep.next?.[0]);
      }}
      onBackClick={props.back}
      sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
    >
      <Box paddingX='min(100px, 6vw)'>
        <Stack gap='24px' maxWidth='720px'>
          <Stack gap='8px'>
            <Typography variant='h6'>AWS PrivateLink Setup</Typography>
            <Typography variant='body2' color='text.secondary'>
              {subtitles[state.format]}
            </Typography>
          </Stack>

          <FormControl size='small' sx={{ maxWidth: '240px' }}>
            <Select
              value={state.format}
              onChange={(e) => {
                const value = e.target.value;
                if (
                  value === 'aws-cli' ||
                  value === 'cloudformation' ||
                  value === 'terraform'
                ) {
                  setWizardState((prev) => ({ ...prev, format: value }));
                }
              }}
            >
              <MenuItem value='aws-cli'>AWS CLI</MenuItem>
              <MenuItem value='cloudformation'>CloudFormation</MenuItem>
              <MenuItem value='terraform' disabled>
                Terraform (coming soon)
              </MenuItem>
            </Select>
          </FormControl>

          {securityGroups.length > 0 && (
            <FormControl size='small' sx={{ maxWidth: '400px' }}>
              <Typography variant='subtitle2' mb='4px'>
                Database Security Group
              </Typography>
              <Select
                value={selectedSgId}
                onChange={(e) => {
                  setWizardState((prev) => ({
                    ...prev,
                    selectedSecurityGroupId: e.target.value,
                  }));
                }}
              >
                {securityGroups.map((sg) => (
                  <MenuItem key={sg.id} value={sg.id}>
                    {sg.name} ({sg.id})
                  </MenuItem>
                ))}
              </Select>
              <Typography variant='body2' color='text.secondary' mt='4px'>
                Select the security group to allow traffic from the resource
                gateway.
              </Typography>
            </FormControl>
          )}

          {loading && (
            <Stack direction='row' alignItems='center' gap='12px'>
              <Loader variant='inline' />
              <Typography variant='body2' color='text.secondary'>
                Loading onboarding instructions...
              </Typography>
            </Stack>
          )}

          {error && (
            <Alert severity='error'>
              {error instanceof Error
                ? error.message
                : 'Failed to load onboarding instructions'}
            </Alert>
          )}

          {currentSteps.length > 0 && (
            <Stack gap='16px'>
              {currentSteps.map(
                (step: PrivateLinkInstructionStep, index: number) => (
                  <Stack key={step.stepNumber} gap='8px'>
                    {state.format === 'cloudformation' && index > 0 && (
                      <Divider sx={{ my: '8px' }}>or</Divider>
                    )}
                    <Typography variant='subtitle2'>{step.title}</Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {step.description}
                    </Typography>
                    {step.command && (
                      <StepCommandContent
                        cmd={substituteRdsSg(step.command)}
                        selectedSgId={selectedSgId}
                        downloadLink={step.downloadLink}
                      />
                    )}
                  </Stack>
                )
              )}
            </Stack>
          )}
        </Stack>
      </Box>
    </StepContainer>
  );
};
